import { Box, Button, Grid, Typography } from "@mui/material";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { mapData } from "../../pages/events/utils/event";
import { getGameSystems, handlePagingAll } from "../api/APIUtilities";
import { BCPCalendarDay, BCPPlus, BCPSearch } from "../icons/bcp/BCPIcons";
import EventCard from "../layout/EventCard";
import InfoContainer from "../layout/InfoContainer";
import ListResponsive from "../other/ListResponsive";
import { AuthContext } from "../user/BCPAuthProvider";
import { postNotification } from "./Notifications";

const REQUIRES_ORGANIZER =
  getEnvironmentVariable("REQUIRES_ORGANIZER") === "true";

export default function EventsPreview(props) {
  const { t } = useTranslation("translation", {
    keyPrefix: "pages.home"
  });

  const [gameSystems, setGameSystems] = useState(null);

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState(null);

  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const searchLimit = props?.searchLimit || 9;

  const getEvents = async () => {
    let _gameSystems = gameSystems;

    if (!_gameSystems) {
      _gameSystems = await getGameSystems({
        inclSystem: true
      });
      setGameSystems(_gameSystems);
    }

    try {
      const gameSystemDict = {};
      _gameSystems.forEach((gameSystem) => {
        gameSystemDict[gameSystem.id] = gameSystem;
      });

      const queryStringParameters = {
        toEvents: true,
        playerEvents: true,
        limit: searchLimit,
        eventEndDateFrom: moment().add(-3, "days").format("YYYY-MM-DDTHH:mm")
      };

      if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
        queryStringParameters.gameSystemId =
          getEnvironmentVariable("SINGLE_GAME_SYSTEM");
      } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
        queryStringParameters.gameSystemManufacturer = getEnvironmentVariable(
          "SINGLE_GAME_MANUFACTURER"
        );
      }

      var data = await handlePagingAll("/events", queryStringParameters);
      data = mapData(data, gameSystemDict, moment);

      setEvents(data);
    } catch (err) {
      postNotification("error", err);
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = (eventId) => {
    var eventArray = [];
    if (events?.length) {
      events.forEach((event) => {
        if (event.id !== eventId) {
          eventArray.push(event);
        }
      });
    }
    setEvents(eventArray);
  };

  useEffect(() => {
    const fetchData = async () => {
      await getEvents();
    };

    fetchData();
    // eslint-disable-next-line
  }, []);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={8}>
        <Typography variant="h3" fontWeight="700">
          {t("myEvents")}
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <Button
          variant="contained"
          href="/organize/events"
          startIcon={<BCPCalendarDay />}
          fullWidth
        >
          {t("viewAllMyEvents")}
        </Button>
      </Grid>

      <Grid item xs={12}>
        {loading || events?.length ? (
          <ListResponsive
            name="My Events"
            originalList={loading ? null : events?.length ? events : null}
            doPages
            initialRowsPerPage={3}
            itemsPerPage={[3]}
            centerNav
            buttonGroup={false}
            rowProps={{
              mapFunction: (row, index) => (
                <Grid key={index} item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                  <EventCard
                    event={row}
                    key={row?.id || index}
                    showStatus
                    organize
                    authContext={authContext}
                    deleteEvent={deleteEvent}
                    preventImageFetch
                  />
                </Grid>
              )
            }}
          />
        ) : !loading && !events?.length ? (
          <InfoContainer
            title={t("noUpcomingEvents")}
            body={t("noUpcomingEventsDescription")}
            ActionButton={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  flexWrap: "wrap"
                }}
              >
                {(!REQUIRES_ORGANIZER ||
                  authContext?.authState?.identity?.isAdmin === true ||
                  authContext?.authState?.identity?.isJudge ||
                  authContext?.authState?.identity?.isOrganizer) && (
                  <Button
                    variant="contained"
                    onClick={(e) => {
                      navigate("/organize/event");
                    }}
                    startIcon={<BCPPlus />}
                  >
                    {t("createEvent")}
                  </Button>
                )}

                <Button
                  variant="outlined"
                  onClick={(e) => {
                    navigate("/play/events");
                  }}
                  startIcon={<BCPSearch />}
                >
                  {t("findEvents")}
                </Button>
              </Box>
            }
            sx={{ mt: 1 }}
          />
        ) : (
          <></>
        )}
      </Grid>
    </Grid>
  );
}
